<template>
    <nav ref="vue-header" :class="headerClass">
        <div class="vue-header-wrapper" :style="{ gap: authed ? '20%' : '40px' }">
            <div
                @mouseover="animateLogo = true"
                @mouseleave="animateLogo = false"
                class="logo"
            >
                <a :href="dashboardLink" @click="onDashboardClick">
                    <img v-if="!animateLogo" :src="URLS.icons.logo" alt="hedlyner-logo" />
                    <AnimatedLogo v-else />
                </a>
            </div>
            <div class="menu-items" v-if="!authed">
                <div v-for="(item, indx) in headerMenuItems" :key="indx" class="item">
                    <a
                        :href="item.target"
                        :target="item._blank ? '_blank' : ''"
                        class="header-links"
                    >
                        {{ item.text }}
                    </a>
                </div>
            </div>
            <div @click="toggleHamburger" class="hamburger-menu">
                <div class="menu-btn" :class="{ 'authed-hamburger': authed }">
                    <img v-if="authed" :src="userAvatar" alt="user-avatar" />
                    <button v-if="!showHamburger" class="hamburger-btn"></button>
                    <button v-else class="close-hamburger-btn"></button>
                </div>
            </div>
            <div v-if="showHamburger" @click="toggleHamburger" class="hamburger-wrapper">
                <div class="hamburger-menu-content">
                    <div v-if="authed" class="content-primary">
                        <div class="profile">
                            <a
                                :href="dashboardLink"
                                @click="onDashboardClick"
                                class="profile-link"
                            >
                                <img :src="userAvatar" alt="user-avatar" />
                                Hi, {{ userFirstName }}
                            </a>
                        </div>
                        <div class="nav-divider"></div>
                    </div>
                    <div v-else class="content-primary">
                        <button
                            v-for="item in headerActionItems"
                            :key="item.text"
                            @click="handleActionClick(item.func)"
                            class="action-link"
                        >
                            {{ item.text }}
                        </button>
                        <div class="nav-divider"></div>
                        <div
                            v-for="item in headerHamburgerItems"
                            :key="item.text"
                            class="hamburger-item"
                            :class="{ 'd-none': width < 600 && item.text === 'Blog' }"
                        >
                            <a :href="item.target" :target="item._blank ? '_blank' : ''">
                                {{ item.text }}
                            </a>
                        </div>
                    </div>
                    <div v-if="authed" class="content-secondary">
                        <div
                            v-for="(item, indx) in authedHeaderHamburgerItems"
                            :key="indx"
                            class="item"
                        >
                            <a
                                :href="item.target"
                                :target="item._blank ? '_blank' : ''"
                                class="header-links"
                            >
                                {{ item.text }}
                            </a>
                            <div v-if="item.divider" class="nav-divider"></div>
                        </div>
                    </div>

                    <div v-if="authed" class="nav-divider"></div>
                    <div v-if="authed" class="content-footer">
                        <button @click="logOut" class="logoff-btn">Log Off</button>
                    </div>
                    <img
                        v-if="width < 600"
                        @click="showHamburger = !showHamburger"
                        :src="getIcon() + 'Delete.png'"
                        class="close-btn"
                    />
                </div>
            </div>
        </div>
        <div v-if="showMobileMenu">
            <SideNav @hideModal="toggleHamburger" />
        </div>
        <BookerAuth
            :show-modal.sync="showAuthModal"
            :init-redirect="initRedirect"
            :authed="authed"
        />
    </nav>
</template>
<script>
    import moment from 'moment'
    import SideNav from './SideNav.vue'
    import BookerAuth from '@/components/auth/booker-register/BookerAuth.vue'
    import AnimatedLogo from '@/components/ui/logo/AnimatedLogo.vue'
    import checkScreen from '@/mixins/check-screen'
    import { api } from '@/helpers/api'
    import { useAuth } from '@/composables/auth/useAuth'
    import { useLocalStorage } from '@/composables/utils/useLocalStorage'

    const URLS = {
        icons: {
            logo: '/images/icons/logo-yellow.svg'
        },
        xhr: {
            checkAuth: '/check-authorization',
            user: '/profile/v1/settings/user',
            logout: '/logout'
        },
        redirect: {
            search: '/search?',
            home: '/',
            venueBooking: '/booking-manager',
            artistBooking: '/booking-manager',
            allProfiles: '/profiles',
            getStarted: '/profile/get-started'
        }
    }
    export default {
        name: 'Header',

        mixins: [checkScreen],

        components: {
            AnimatedLogo,
            SideNav,
            BookerAuth
        },
        props: {
            background: {
                required: false,
                type: Boolean,
                default: true
            }
        },

        setup() {
            const { showAuthModal } = useAuth()
            const { localGet } = useLocalStorage()

            return {
                showAuthModal,
                localGet
            }
        },

        data: () => ({
            authed: false,

            animateLogo: false,

            scrollY: 0,

            headerHeight: 0,

            showHamburger: false,

            csrf: document.head.querySelector('meta[name="csrf-token"]')['content'],

            formValues: {
                keywords: '',
                location: '',
                city: '',
                lat: '',
                lon: '',
                date: ''
            },
            datepickerOptions: {
                language: 'en',
                format: 'D, MM dd'
            },
            menuItems: [
                {
                    category: 'menu',
                    text: 'For Artists',
                    target: '/for-artists'
                },
                {
                    category: 'menu',
                    text: 'For Bookers',
                    target: '/for-bookers'
                },
                {
                    category: 'menu',
                    text: 'Explore',
                    target: '/exploration'
                },
                {
                    category: 'hamburger',
                    text: 'Explore',
                    target: '/exploration'
                },
                {
                    category: 'hamburger',
                    text: 'FAQs',
                    target: '/faqs',
                    authed: true
                },
                {
                    category: 'hamburger',
                    text: 'About Us',
                    target: '/about-us',
                    authed: true
                },
                {
                    category: 'hamburger',
                    text: 'Contact Us',
                    target: '/contact-us',
                    authed: true
                },
                {
                    category: 'action',
                    text: 'Log In',
                    target: null,
                    func: 'handleBookerRegister'
                },
                {
                    category: 'action',
                    text: 'Sign Up',
                    target: null,
                    func: 'handleBookerRegister'
                }
            ],

            mapData: {
                autocomplete: null
            },

            userInfo: {},

            apiLoaded: false,
            checkInterval: null,
            showMobileMenu: false,
            initRedirect: false
        }),

        methods: {
            handleBookerRegister() {
                this.showAuthModal = true
            },
            getIcon() {
                return '/images/'
            },
            // NOTE: this needs to be the same name as callback defined in GoogleMapsApiController.php
            initialize() {
                // eslint-disable-next-line
                this.mapData.autocomplete = new google.maps.places.Autocomplete(
                    this.$refs['autocomplete'],
                    {
                        fields: ['geometry', 'formatted_address'],
                        types: ['geocode', 'establishment']
                    }
                )

                this.mapData.autocomplete.addListener('place_changed', this.onPlaceChanged)
            },

            onPlaceChanged() {
                let place = this.mapData.autocomplete.getPlace()

                if (place.geometry && place.formatted_address) {
                    let lat = place.geometry.location.lat()
                    let lng = place.geometry.location.lng()

                    this.formValues.location = place.formatted_address
                    this.formValues.city = place.formatted_address
                    this.formValues.lat = lat
                    this.formValues.lon = lng
                }
            },

            toggleHamburger() {
                if (this.width <= 1000 && this.authed) {
                    this.showMobileMenu = !this.showMobileMenu
                } else {
                    if (this.showHamburger) {
                        this.showHamburger = false
                    } else {
                        this.showHamburger = true
                    }
                }
            },
            onScroll() {
                this.scrollY = window.top.scrollY
            },

            async logOut() {
                await api({
                    method: 'post',
                    url: this.URLS.xhr.logout,
                    data: {
                        token: this.csrf
                    }
                })
                window.location.href = URLS.redirect.home
            },

            submitQuery() {
                let params = new URLSearchParams({
                    location: this.formValues.location,
                    city: this.formValues.city,
                    lat: this.formValues.lat,
                    lon: this.formValues.lon,
                    search: this.formValues.keywords,
                    date: this.formValues.date
                })
                window.location.href = URLS.redirect.search + params
            },

            async isAuthed() {
                const data = await api(
                    {
                        method: 'get',
                        url: this.URLS.xhr.checkAuth
                    },
                    { error: false }
                )

                this.authed = data.isAuthorized
            },

            async getUserSettings() {
                const data = await api({
                    method: 'get',
                    url: this.URLS.xhr.user
                })
                this.userInfo = data.data[0]
            },
            setDate() {
                const today = new Date()
                const tomorrow = new Date(today)
                tomorrow.setDate(today.getDate() + 1)
                const datePickerInput = $('#date')
                datePickerInput.datepicker({
                    ...this.datepickerOptions,
                    startDate: tomorrow,
                    format: 'MM d yyyy'
                })
                datePickerInput.on('changeDate', (e) => {
                    const selectedDate = e.date
                    if (selectedDate < tomorrow) {
                        datePickerInput.datepicker('setDate', tomorrow)
                        this.eventDate = moment(tomorrow).format('MM d yyyy')
                    } else {
                        this.eventDate = moment(selectedDate).format('MM d yyyy')
                    }
                })
            },

            handleActionClick(func) {
                this[func]()
            },

            onDashboardClick(e) {
                e.preventDefault()

                if (this.authed && this.userInfo?.hasEvents) {
                    window.location.href = this.URLS.redirect.venueBooking
                } else if (this.authed) {
                    this.showAuthModal = true
                    this.initRedirect = true
                } else if (!this.authed) {
                    this.showAuthModal = true
                }

                return false
            },

            launchRegisterIfRequested() {
                let params

                try {
                    params = new URL(window.location.toString()).searchParams
                } catch {
                    return
                }

                const register = JSON.parse(params.get('openRegistration'))

                if (register) {
                    this.handleBookerRegister()
                }
            }
        },

        computed: {
            URLS: () => URLS,
            headerMenuItems: (self) => self.menuItems.filter((i) => i.category === 'menu'),

            headerHamburgerItems: (self) =>
                self.menuItems.filter((i) => i.category === 'hamburger'),
            authedHeaderHamburgerItems: (self) =>
                self.menuItems.filter((i) => i.category === 'hamburger' && i.authed),
            headerActionItems: (self) => self.menuItems.filter((i) => i.category === 'action'),

            dashboardLink: (self) => {
                return self.URLS.redirect.home
            },

            userAvatar: (self) => {
                if (self.userInfo?.avatar) {
                    if (self.userInfo.avatar[0] !== '/') {
                        return '/' + self.userInfo.avatar
                    }

                    return self.userInfo.avatar
                } else {
                    return '/images/icons/side-nav/profile.png'
                }
            },

            userFirstName: (self) =>
                self.userInfo.first_name ? self.userInfo.first_name : 'Hedlyner user',

            headerClass: (self) => {
                let _class = {
                    'vue-header-container': true
                }
                if (self.background || self.scrollY > self.headerHeight) {
                    _class['with-background'] = true
                }

                return _class
            }
        },

        watch: {
            apiLoaded: function (loaded) {
                if (loaded) {
                    clearInterval(this.checkInterval)
                }
            }
        },

        async created() {
            await this.isAuthed()
            if (this.authed) {
                await this.getUserSettings()
            } else {
                this.launchRegisterIfRequested()
            }
            this.formValues.city =
                localStorage.getItem('currentLocation') !== 'null'
                    ? localStorage.getItem('currentLocation')
                    : ''
        },
        mounted() {
            this.checkInterval = setInterval(() => {
                if (window.google) {
                    this.apiLoaded = true
                    this.initialize()
                    this.setDate()
                }
            })
            window.addEventListener('scroll', this.onScroll)
            this.headerHeight = this.$refs['vue-header'].clientHeight + 5

            const bookerRegister = this.localGet('booker-register-modal', {
                remove: true,
                fallback: false
            })

            if (bookerRegister) {
                this.showAuthModal = true
                this.initRedirect = true
            }
        },

        beforeDestroy() {
            window.removeEventListener('scroll', this.onScroll)
            localStorage.removeItem('currentLocation')
            clearInterval(this.paginationTimer)
        }
    }
</script>
<style lang="scss" scoped>
    $smallMobile: 350px;
    $mobile: 550px;
    $tablet: 650px;
    $bigTablet: 1000px;
    $laptop: 1441px;
    $bigLaptop: 1600px;
    $desktop: 2400px;
    $ultrawide: 2800px;

    @media screen and (min-width: $ultrawide) {
        .vue-header-container {
            .vue-header-wrapper {
                .discover-artist {
                    margin-left: 504px !important;
                }
            }
        }
    }
    @media screen and (min-width: $desktop) and (max-width: $ultrawide) {
        .vue-header-container {
            .vue-header-wrapper {
                .discover-artist {
                    margin-left: 252px !important;
                }
            }
        }
    }
    @media screen and (min-width: $bigLaptop) and (max-width: $desktop) {
        .vue-header-container {
            .vue-header-wrapper {
                .discover-artist {
                    margin-left: 126px !important;
                }
            }
        }
    }
    @media screen and (min-width: $laptop) and (max-width: $bigLaptop) {
        .vue-header-container {
            .vue-header-wrapper {
                .discover-artist {
                    margin-left: 13px !important;
                }
            }
        }
    }
    @media screen and (max-width: $tablet) {
        .vue-header-container {
            .vue-header-wrapper {
                .menu-items {
                    display: none !important;
                }
            }
        }
    }

    @media screen and (max-width: $bigTablet) and (min-width: $tablet) {
        .vue-header-container {
            .vue-header-wrapper {
                gap: 20px !important;
            }
        }
        .menu-items {
            gap: 15px !important;
        }
        .discover-artist {
            margin-left: 0px !important;
        }
    }

    @media screen and (max-width: $mobile) {
        .vue-header-container {
            .hamburger-wrapper {
                left: 0;
                background: #232323 !important;
                .hamburger-menu-content {
                    left: 0 !important;
                    top: 0 !important;
                    min-width: 92%;
                    margin: 16px;
                    .content-primary {
                        width: 100%;
                    }
                }
            }
            .vue-header-wrapper {
                padding: 11px 16px !important;
                gap: 16px !important;
                justify-content: space-between !important;

                .discover-artist {
                    margin-left: 0 !important;
                    gap: 16px !important;
                    justify-content: space-between !important;
                    max-width: 230px;
                    width: 100%;
                    overflow: hidden;
                    text-overflow: ellipse;

                    span {
                        font-size: 12px !important;
                    }
                }
                .hamburger-menu {
                    margin-left: 0 !important;
                }
            }
        }
    }
    @media screen and (max-width: $smallMobile) {
        .discover-artist {
            max-width: 100px !important;
        }
    }

    .close-btn {
        max-width: 14px;
        max-height: 14px;
        position: absolute;
        right: 12px;
        top: 10px;
    }
    .vue-header-container {
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 120;

        a {
            color: #dedede;
            font-family: Instrument Sans;
            font-size: 14px;
            font-style: normal;
            font-weight: 600;
            line-height: 130%;
            letter-spacing: 0.14px;
        }
        a:link {
            text-decoration: inherit;
        }

        a:visited {
            text-decoration: inherit;
        }

        a:hover {
            color: #cdeb09 !important;
        }

        .vue-header-wrapper {
            transition: background-color 0.3s;
            height: 72px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            padding: 15px 40px;
            background-color: transparent;

            .logo {
                position: absolute;
                left: 40px;
                top: 16px;
                cursor: pointer;

                img {
                    width: 37px;
                    height: 40px;
                }
            }

            .menu-items {
                flex: 1 1 auto;
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                gap: 20px;
                max-width: 322px;

                .item {
                    width: 100%;
                    text-wrap: nowrap;
                }
            }

            .hamburger-menu {
                position: absolute;
                top: 16px;
                right: 40px;

                .menu-btn {
                    display: flex;
                    border-radius: 120px;
                    border: 1px solid #dedede;
                    padding: 10px;
                    align-items: center;
                    justify-content: center;
                    background: #121212;

                    img {
                        margin-right: 10px;
                        width: 32px;
                        height: 32px;
                        border-radius: 50%;
                        object-fit: cover;
                    }
                }

                .menu-btn.authed-hamburger {
                    padding: 4px 16px 4px 4px;
                }
                .hamburger-btn {
                    background: url('/images/icons/header/hamburger-menu.svg') no-repeat;
                    width: 20px;
                    height: 20px;
                    border: none;
                }
                .close-hamburger-btn {
                    background: url('/images/icons/header/close-menu.svg') no-repeat;
                    width: 20px;
                    height: 20px;
                    border: none;
                }
            }
        }
        .hamburger-wrapper {
            position: absolute;
            top: 0;
            background: transparent;
            width: 100vw;
            height: 100vh;

            a {
                color: #acacac;
            }

            .hamburger-menu-content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                gap: 4px;
                background: #121212;
                position: relative;
                padding-top: 8px;
                padding-bottom: 16px;
                border-radius: 20px;
                max-width: 160px;
                min-height: fit-content;
                left: calc(100vw - 210px);
                top: 75px;
                z-index: 9999;

                .nav-divider {
                    width: 100%;
                    height: 1px;
                    margin-top: 4px;
                    margin-bottom: 4px;
                    background: #262626;
                }

                .profile {
                    color: #acacac;
                    font-family: Instrument Sans;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 230%;
                    letter-spacing: 0.14px;
                    padding: 8px 16px;

                    .profile-link {
                        display: flex;
                        justify-content: flex-start;
                        align-items: center;
                        gap: 8px;

                        img {
                            width: 32px;
                            height: 32px;
                            border-radius: 50%;
                            object-fit: cover;
                        }
                    }
                }

                .content-primary,
                .content-secondary {
                    display: flex;
                    flex-direction: column;
                    width: 100%;

                    .item {
                        padding: 8px 16px;
                    }
                }

                .action-link {
                    color: #cdeb09;
                    background: none;
                    border: none;
                    font-family: Instrument Sans;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 130%;
                    letter-spacing: 0.14px;
                    text-align: left;
                    padding: 8px 16px;
                }

                .hamburger-item {
                    padding: 8px 16px;
                }

                .logoff-btn {
                    color: #acacac;
                    font-family: Instrument Sans;
                    font-size: 14px;
                    font-style: normal;
                    font-weight: 600;
                    line-height: 130%;
                    letter-spacing: 0.14px;
                    border: none;
                    background: none;
                    padding: 8px 16px;
                }

                .logoff-btn:hover {
                    color: #cdeb09;
                }
            }
        }
    }

    .vue-header-container.with-background {
        .vue-header-wrapper {
            transition: background-color 0.3s;
            border-bottom: 1px solid #333;
            background: #121212;

            a {
                color: #8b8b8b;
            }

            .discover-artist {
                input {
                    color: #dedede;
                }
                input::placeholder {
                    color: #acacac;
                }
            }
        }
    }
</style>
